import React, { useState, useEffect } from 'react';
import { Image, Save, Eye, EyeOff, Upload, Link, Trash2 } from 'lucide-react';

function AdminPopupPromo() {
  const [config, setConfig] = useState({
    activo: false,
    imagen_url: '',
    imagen_url_mobile: '',
    link_destino: '',
    titulo: 'Promoción'
    });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewImageMobile, setPreviewImageMobile] = useState(null);
  const [uploadingMobile, setUploadingMobile] = useState(false);
  const [message, setMessage] = useState(null);

  // Cargar configuración actual
  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/popup-promo`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error cargando config:', error);
    } finally {
      setLoading(false);
    }
  };

  // Manejar selección de imagen (desktop o mobile)
    const handleImageSelect = (e, tipo = 'desktop') => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo
    if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Solo se permiten imágenes' });
        return;
    }

    // Validar tamaño (máx 5MB)
    if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'La imagen no puede superar 5MB' });
        return;
    }

    // Convertir a base64 para preview y subida
    const reader = new FileReader();
    reader.onload = (event) => {
        if (tipo === 'mobile') {
        setPreviewImageMobile(event.target.result);
        } else {
        setPreviewImage(event.target.result);
        }
    };
    reader.readAsDataURL(file);
    };

  // Subir imagen (desktop o mobile)
    const handleUploadImage = async (tipo = 'desktop') => {
    const imageData = tipo === 'mobile' ? previewImageMobile : previewImage;
    if (!imageData) return;

    if (tipo === 'mobile') {
        setUploadingMobile(true);
    } else {
        setUploading(true);
    }
    setMessage(null);

    try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/popup-promo/upload`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
            imagen_base64: imageData,
            tipo: tipo // 'desktop' o 'mobile'
        })
        });

        const data = await response.json();

        if (response.ok && data.success) {
        if (tipo === 'mobile') {
            setConfig(prev => ({ ...prev, imagen_url_mobile: data.imagen_url }));
            setPreviewImageMobile(null);
        } else {
            setConfig(prev => ({ ...prev, imagen_url: data.imagen_url }));
            setPreviewImage(null);
        }
        setMessage({ type: 'success', text: `Imagen ${tipo} subida correctamente` });
        } else {
        setMessage({ type: 'error', text: data.error || 'Error subiendo imagen' });
        }
    } catch (error) {
        setMessage({ type: 'error', text: 'Error de conexión' });
    } finally {
        if (tipo === 'mobile') {
        setUploadingMobile(false);
        } else {
        setUploading(false);
        }
    }
    };

  // Guardar configuración
  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/popup-promo`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          activo: config.activo,
          link_destino: config.link_destino,
          titulo: config.titulo
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: 'Configuración guardada' });
      } else {
        setMessage({ type: 'error', text: data.error || 'Error guardando' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="animate-pulse">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-purple-100 p-3 rounded-xl">
          <Image size={24} className="text-purple-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Popup Promocional</h2>
          <p className="text-sm text-gray-600">Configura un popup con imagen promocional</p>
        </div>
      </div>

      {/* Mensaje */}
      {message && (
        <div className={`mb-4 p-3 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-300' 
            : 'bg-red-100 text-red-800 border border-red-300'
        }`}>
          {message.text}
        </div>
      )}

      <div className="space-y-6">
        {/* Toggle Activo/Inactivo */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
          <div className="flex items-center gap-3">
            {config.activo ? (
              <Eye size={20} className="text-green-600" />
            ) : (
              <EyeOff size={20} className="text-gray-400" />
            )}
            <div>
              <span className="font-medium text-gray-900">Estado del Popup</span>
              <p className="text-sm text-gray-600">
                {config.activo ? 'Visible para los visitantes' : 'Oculto'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setConfig(prev => ({ ...prev, activo: !prev.activo }))}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              config.activo ? 'bg-green-500' : 'bg-gray-300'
            }`}
          >
            <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
              config.activo ? 'translate-x-8' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {/* Subir Imágenes - Desktop y Mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* DESKTOP */}
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
            🖥️ Imagen Desktop
            </label>
            
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-4">
            {(previewImage || config.imagen_url) && (
                <div className="mb-4 relative">
                <img 
                    src={previewImage || config.imagen_url} 
                    alt="Preview Desktop" 
                    className="max-w-full max-h-48 mx-auto rounded-lg shadow"
                />
                {previewImage && (
                    <button
                    onClick={() => setPreviewImage(null)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                    <Trash2 size={16} />
                    </button>
                )}
                </div>
            )}

            <div className="flex flex-col items-center gap-3">
                <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageSelect(e, 'desktop')}
                className="hidden"
                id="popup-image-desktop"
                />
                <label
                htmlFor="popup-image-desktop"
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors text-sm"
                >
                <Upload size={18} />
                <span>Seleccionar</span>
                </label>
                
                {previewImage && (
                <button
                    onClick={() => handleUploadImage('desktop')}
                    disabled={uploading}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors text-sm"
                >
                    {uploading ? 'Subiendo...' : 'Confirmar'}
                </button>
                )}
                
                <p className="text-xs text-gray-500 text-center">
                Horizontal. Recomendado: 800x500px
                </p>
            </div>
            </div>
        </div>

        {/* MOBILE */}
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
            📱 Imagen Mobile
            </label>
            
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-4">
            {(previewImageMobile || config.imagen_url_mobile) && (
                <div className="mb-4 relative">
                <img 
                    src={previewImageMobile || config.imagen_url_mobile} 
                    alt="Preview Mobile" 
                    className="max-w-full max-h-48 mx-auto rounded-lg shadow"
                />
                {previewImageMobile && (
                    <button
                    onClick={() => setPreviewImageMobile(null)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                    <Trash2 size={16} />
                    </button>
                )}
                </div>
            )}

            <div className="flex flex-col items-center gap-3">
                <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageSelect(e, 'mobile')}
                className="hidden"
                id="popup-image-mobile"
                />
                <label
                htmlFor="popup-image-mobile"
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors text-sm"
                >
                <Upload size={18} />
                <span>Seleccionar</span>
                </label>
                
                {previewImageMobile && (
                <button
                    onClick={() => handleUploadImage('mobile')}
                    disabled={uploadingMobile}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors text-sm"
                >
                    {uploadingMobile ? 'Subiendo...' : 'Confirmar'}
                </button>
                )}
                
                <p className="text-xs text-gray-500 text-center">
                Vertical. Recomendado: 400x700px
                </p>
            </div>
            </div>
        </div>
        </div>

        {/* Link de destino */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Link size={16} className="inline mr-1" />
            Link de destino (opcional)
          </label>
          <input
            type="text"
            value={config.link_destino}
            onChange={(e) => setConfig(prev => ({ ...prev, link_destino: e.target.value }))}
            placeholder="Ej: /paquetes/brasil o https://..."
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Al hacer clic en la imagen, el usuario irá a este link
          </p>
        </div>

        {/* Título (para accesibilidad) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Título de la promoción
          </label>
          <input
            type="text"
            value={config.titulo}
            onChange={(e) => setConfig(prev => ({ ...prev, titulo: e.target.value }))}
            placeholder="Ej: Ofertas de Verano"
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>

        {/* Botón Guardar */}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 bg-[#2AA8B0] text-white py-3 rounded-xl font-semibold hover:bg-[#1E8A91] disabled:opacity-50 transition-colors"
        >
          <Save size={20} />
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </button>
      </div>
    </div>
  );
}

export default AdminPopupPromo;